/**
 * SipClient — framework-agnostic wrapper around JsSIP.
 *
 * Owns the UA (WebSocket + REGISTER), all RTCSessions, local microphone streams and remote
 * audio playback. Exposes an immutable snapshot for React (useSyncExternalStore) plus
 * imperative call-control methods.
 */
import * as JsSIP from 'jssip';
import type { RTCSession, EndEvent, IceCandidateEvent } from 'jssip/lib/RTCSession';
import type { IncomingRTCSessionEvent, OutgoingRTCSessionEvent } from 'jssip/lib/UA';
import type { Subscriber } from 'jssip/lib/Subscriber';
import { toSipTarget } from '../lib/phone';
import { parseMessageSummary, type Voicemail } from '../lib/mwi';
import { tones } from '../lib/tones';

export type RegState = 'idle' | 'connecting' | 'connected' | 'registered' | 'failed';

export type CallState =
  | 'incoming' // ringing here, not yet answered
  | 'dialing' // INVITE sent, no provisional response yet
  | 'ringing' // 18x received
  | 'answering' // user pressed answer, media being set up
  | 'active'
  | 'ended';

export interface CallInfo {
  id: string;
  direction: 'incoming' | 'outgoing';
  number: string; // user part of remote URI (or the dialled string)
  name: string; // display name if the far end sent one
  state: CallState;
  muted: boolean;
  held: boolean; // held by us
  remoteHeld: boolean; // held by the far end
  earlyMedia: boolean; // far end is streaming ringback / announcements
  startedAt: number;
  answeredAt?: number;
  endedAt?: number;
  endCause?: string;
  endedBy?: 'local' | 'remote' | 'system';
  transfer?: { target: string; status: 'pending' | 'accepted' | 'failed' };
}

export interface ClientConfig {
  server: string;
  domain: string;
  username: string;
  /** Either a plaintext password or (preferred) a digest HA1 = MD5(user:realm:password) plus its realm. */
  password?: string;
  ha1?: string;
  realm?: string;
  displayName?: string;
  inputDeviceId?: string;
  outputDeviceId?: string;
  sipTrace?: boolean;
}

export interface Snapshot {
  reg: RegState;
  regError?: string;
  calls: CallInfo[];
  /** Mailbox state from MWI (SUBSCRIBE message-summary); null until the PBX first reports. */
  voicemail: Voicemail | null;
}

export interface CallStats {
  codec?: string;
  clockRate?: number;
  rttMs?: number;
  jitterMs?: number;
  packetsReceived: number;
  packetsLost: number;
  packetsSent: number;
  bytesReceived: number;
  audioLevel?: number; // 0..1 remote level
  totalAudioEnergy?: number;
  localCandidateType?: string;
  dtlsState?: string; // 'connected' once media keys are negotiated
  srtpCipher?: string; // e.g. AES_CM_128_HMAC_SHA1_80 / AEAD_AES_128_GCM
}

export interface CallEndedEvent {
  call: CallInfo;
}

interface Internal {
  session: RTCSession;
  audio: HTMLAudioElement;
  localStream?: MediaStream;
  info: CallInfo;
  iceTimer?: number;
  dtmfQueue?: string;
  dtmfTimer?: number;
}

/**
 * bundlePolicy stays 'balanced': FreeSWITCH answers without a=mid / a=group:BUNDLE, and with
 * 'max-bundle' Chrome rejects the 200 OK after a 183 ("Answer cannot remove m= section ... from
 * already-established BUNDLE group").
 */
const PC_CONFIG: RTCConfiguration = {
  iceServers: [{ urls: ['stun:stun.l.google.com:19302', 'stun:stun.cloudflare.com:3478'] }],
  bundlePolicy: 'balanced',
  rtcpMuxPolicy: 'require',
};

/**
 * Session-Expires for RFC 4028 session timers. FreeSWITCH rejects JsSIP's default of 90s with
 * "422 Session Interval Too Small" (and JsSIP doesn't retry), so use a value above its Min-SE.
 */
const SESSION_EXPIRES = 300;

/** Once a publicly reachable candidate exists, wait this long for siblings before sending. */
const ICE_SETTLE_MS = 100;
/**
 * Hard cap while we only have private candidates. FreeSWITCH rejects an offer whose only candidates
 * are private (488 INCOMPATIBLE_DESTINATION), so it's worth waiting for STUN — a cold first lookup
 * can take >1s. JsSIP also sends as soon as gathering completes, so this only bites if STUN hangs.
 */
const ICE_MAX_MS = 6000;

/** RFC 1918 / CGNAT / link-local / loopback / mDNS (.local) — not reachable by the PBX. */
function isPrivateCandidate(address: string | null | undefined): boolean {
  if (!address) return true;
  if (address.endsWith('.local')) return true;
  if (address.includes(':')) return /^(fe80|fc|fd|::1)/i.test(address);
  const [a, b] = address.split('.').map(Number);
  return a === 10 || a === 127 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168) || (a === 169 && b === 254) || (a === 100 && b >= 64 && b <= 127);
}

export const AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  channelCount: 1,
};

type Listener = () => void;
type EndListener = (e: CallEndedEvent) => void;

export class SipClient {
  private ua: JsSIP.UA | null = null;
  private cfg: ClientConfig | null = null;
  private calls = new Map<string, Internal>();
  private listeners = new Set<Listener>();
  private endListeners = new Set<EndListener>();
  private snapshot: Snapshot = { reg: 'idle', calls: [], voicemail: null };
  private mwi: Subscriber | null = null;
  /** Per-call output override (in-call Speaker toggle); cleared when the last call ends. */
  private callOutput: string | null = null;
  private mwiRetry: number | undefined;
  private onlineHandler = () => this.handleOnline();

  // ---------- store plumbing ----------

  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };

  getSnapshot = (): Snapshot => this.snapshot;

  onCallEnded(fn: EndListener): () => void {
    this.endListeners.add(fn);
    return () => this.endListeners.delete(fn);
  }

  private emit(patch: Partial<Snapshot> = {}): void {
    this.snapshot = {
      ...this.snapshot,
      ...patch,
      calls: [...this.calls.values()].map((c) => ({ ...c.info })),
    };
    this.listeners.forEach((l) => l());
  }

  private update(id: string, patch: Partial<CallInfo>): void {
    const c = this.calls.get(id);
    if (!c) return;
    c.info = { ...c.info, ...patch };
    this.emit();
    this.syncTones();
  }

  // ---------- registration ----------

  connect(cfg: ClientConfig): void {
    // Same account, already up: nothing to do (avoids a needless re-register).
    if (this.ua && this.cfg && JSON.stringify(this.cfg) === JSON.stringify(cfg) && this.snapshot.reg !== 'failed') return;
    this.disconnect();
    this.cfg = cfg;
    if (cfg.sipTrace) JsSIP.debug.enable('JsSIP:*');
    else JsSIP.debug.disable();

    const socket = new JsSIP.WebSocketInterface(cfg.server);
    const ua = new JsSIP.UA({
      sockets: [socket],
      uri: `sip:${cfg.username}@${cfg.domain}`,
      authorization_user: cfg.username,
      ...(cfg.ha1 ? { ha1: cfg.ha1, realm: cfg.realm || cfg.domain } : { password: cfg.password }),
      display_name: cfg.displayName || undefined,
      register: true,
      register_expires: 300,
      session_timers: true,
      connection_recovery_min_interval: 2,
      connection_recovery_max_interval: 15,
      user_agent: 'NGN-WebPhone/0.1 JsSIP',
    });
    this.ua = ua;

    ua.on('connecting', () => this.emit({ reg: 'connecting', regError: undefined }));
    ua.on('connected', () => {
      if (this.snapshot.reg !== 'registered') this.emit({ reg: 'connected' });
    });
    ua.on('disconnected', (e) => {
      if (this.ua !== ua) return;
      this.emit({
        reg: 'connecting',
        regError: e.error ? `Connection lost${e.code ? ` (${e.code})` : ''} — retrying` : undefined,
      });
    });
    let warmed = false;
    ua.on('registered', () => {
      this.emit({ reg: 'registered', regError: undefined });
      if (!warmed) {
        warmed = true;
        this.warmIce();
      }
      if (!this.mwi) {
        try {
          this.subscribeMwi(ua, cfg);
        } catch (err) {
          console.warn('MWI subscribe failed', err); // voicemail badge falls back to the API list
        }
      }
    });
    // FreeSWITCH may also send MWI as an unsolicited NOTIFY (out of any subscription dialog).
    ua.on('sipEvent', (e) => {
      const { event, request } = e as unknown as { event?: { event?: string }; request: { body?: string } };
      if (event?.event === 'message-summary' && request.body) this.onMessageSummary(request.body);
    });
    ua.on('unregistered', () => {
      if (this.ua === ua) this.emit({ reg: ua.isConnected() ? 'connected' : 'connecting' });
    });
    ua.on('registrationFailed', (e) => {
      const status = e.response?.status_code;
      const reason = status ? `${status} ${e.response.reason_phrase}` : e.cause;
      this.emit({ reg: 'failed', regError: `Registration failed: ${reason}` });
    });
    ua.on('newRTCSession', (e: IncomingRTCSessionEvent | OutgoingRTCSessionEvent) => {
      if (e.originator === 'remote') this.handleIncoming(e.session);
    });

    window.addEventListener('online', this.onlineHandler);
    this.emit({ reg: 'connecting', regError: undefined });
    ua.start();
  }

  /** Voicemail waiting indicator for our own mailbox (RFC 3842). */
  private subscribeMwi(ua: JsSIP.UA, cfg: ClientConfig): void {
    window.clearTimeout(this.mwiRetry);
    const sub = ua.subscribe(`sip:${cfg.username}@${cfg.domain}`, 'message-summary', 'application/simple-message-summary', {
      expires: 3600,
      contentType: 'application/simple-message-summary',
    });
    this.mwi = sub;
    sub.on('notify', (_final, _request, body) => body && this.onMessageSummary(body));
    sub.on('terminated', () => {
      if (this.mwi !== sub) return;
      this.mwi = null;
      // Re-subscribe (the PBX may have restarted, or the WebSocket dropped).
      this.mwiRetry = window.setTimeout(() => {
        if (this.ua === ua && ua.isRegistered()) this.subscribeMwi(ua, cfg);
      }, 30_000);
    });
    sub.subscribe();
  }

  private onMessageSummary(body: string): void {
    const vm = parseMessageSummary(body);
    if (vm) this.emit({ voicemail: vm });
  }

  disconnect(): void {
    window.clearTimeout(this.mwiRetry);
    const mwi = this.mwi;
    this.mwi = null;
    try {
      mwi?.terminate();
    } catch {
      /* transport already gone */
    }
    window.removeEventListener('online', this.onlineHandler);
    const ua = this.ua;
    this.ua = null;
    if (ua) {
      ua.removeAllListeners();
      for (const c of this.calls.values()) {
        try {
          c.session.terminate();
        } catch {
          /* already gone */
        }
        this.cleanup(c.info.id, 'Disconnected', 'local');
      }
      ua.stop();
    }
    this.emit({ reg: 'idle', regError: undefined, voicemail: null });
  }

  /** Network came back: re-register immediately instead of waiting for JsSIP's backoff. */
  private handleOnline(): void {
    const ua = this.ua;
    if (!ua) return;
    if (ua.isConnected()) ua.register();
    else if (this.calls.size === 0 && this.cfg) this.connect(this.cfg);
  }

  get isRegistered(): boolean {
    return this.snapshot.reg === 'registered';
  }

  // ---------- media ----------

  private async getMic(): Promise<MediaStream> {
    const deviceId = this.cfg?.inputDeviceId;
    return navigator.mediaDevices.getUserMedia({
      audio: { ...AUDIO_CONSTRAINTS, ...(deviceId ? { deviceId: { ideal: deviceId } } : {}) },
      video: false,
    });
  }

  private createAudio(): HTMLAudioElement {
    const audio = new Audio();
    audio.autoplay = true;
    const sinkId = this.callOutput ?? this.cfg?.outputDeviceId;
    const a = audio as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
    if (sinkId && a.setSinkId) a.setSinkId(sinkId).catch(() => undefined);
    return audio;
  }

  /** Wire remote audio + fast ICE policy onto a session's peer connection. */
  private attachPeer(c: Internal, pc: RTCPeerConnection): void {
    const play = () => {
      const streams = new MediaStream();
      pc.getReceivers().forEach((r) => r.track && r.track.kind === 'audio' && streams.addTrack(r.track));
      if (streams.getTracks().length === 0) return;
      c.audio.srcObject = streams;
      c.audio.play().catch(() => undefined);
    };
    pc.addEventListener('track', play);
    play();
  }

  /**
   * JsSIP only sends the INVITE / 200 once ICE gathering is *complete*, which can take 10s+ on hosts
   * with VPN/virtual adapters. Send as soon as we have a candidate the PBX can actually reach
   * (server-reflexive, relay, or a public host address); with only private candidates keep waiting
   * for STUN, up to ICE_MAX_MS.
   */
  private fastIce(c: Internal): (e: IceCandidateEvent) => void {
    let started = false;
    let reachable = false;
    return (e) => {
      if (!started) {
        started = true;
        window.clearTimeout(c.iceTimer);
        c.iceTimer = window.setTimeout(e.ready, ICE_MAX_MS);
      }
      if (reachable) return;
      const cand = e.candidate;
      const type = cand.type ?? (/ typ (\w+)/.exec(cand.candidate)?.[1] || '');
      const address = cand.address ?? cand.candidate.split(' ')[4];
      if (type === 'srflx' || type === 'relay' || (type === 'host' && !isPrivateCandidate(address))) {
        reachable = true;
        window.clearTimeout(c.iceTimer);
        c.iceTimer = window.setTimeout(e.ready, ICE_SETTLE_MS);
      }
    };
  }

  /**
   * Resolve the STUN servers once in the background after registering, so the first real call
   * doesn't pay for a cold DNS + STUN round trip.
   */
  private warmIce(): void {
    try {
      const pc = new RTCPeerConnection(PC_CONFIG);
      pc.createDataChannel('warmup');
      const done = () => pc.close();
      const timer = window.setTimeout(done, 5000);
      pc.onicecandidate = (e) => {
        if (!e.candidate || e.candidate.type === 'srflx') {
          window.clearTimeout(timer);
          done();
        }
      };
      pc.createOffer().then((o) => pc.setLocalDescription(o)).catch(done);
    } catch {
      /* best effort */
    }
  }

  // ---------- calls ----------

  private track(session: RTCSession, info: CallInfo, localStream?: MediaStream): Internal {
    const c: Internal = { session, audio: this.createAudio(), info, localStream };
    this.calls.set(info.id, c);
    const id = info.id;

    session.on('peerconnection', (e) => this.attachPeer(c, e.peerconnection));
    if (session.connection) this.attachPeer(c, session.connection);
    session.on('icecandidate', this.fastIce(c));
    // Be the DTLS client on calls we offer (see preferDtlsClient).
    session.on('sdp', (e) => {
      if (e.originator === 'local' && e.type === 'offer') e.sdp = preferDtlsClient(e.sdp);
    });

    session.on('progress', (e: unknown) => {
      if (info.direction !== 'outgoing') return;
      const res = (e as { response?: { status_code: number; body?: string } }).response;
      const early = Boolean(res?.body);
      this.update(id, { state: 'ringing', earlyMedia: early || this.calls.get(id)!.info.earlyMedia });
    });
    session.on('accepted', () => this.update(id, { state: 'active', answeredAt: Date.now() }));
    session.on('confirmed', () => {
      const cur = this.calls.get(id)?.info;
      if (cur && cur.state !== 'active') this.update(id, { state: 'active', answeredAt: cur.answeredAt ?? Date.now() });
    });
    session.on('hold', (e) => this.update(id, e.originator === 'local' ? { held: true } : { remoteHeld: true }));
    session.on('unhold', (e) => this.update(id, e.originator === 'local' ? { held: false } : { remoteHeld: false }));
    session.on('muted', () => this.update(id, { muted: true }));
    session.on('unmuted', () => this.update(id, { muted: false }));
    session.on('ended', (e: EndEvent) => this.cleanup(id, e.cause, e.originator));
    session.on('failed', (e: EndEvent) => this.cleanup(id, e.cause, e.originator));
    // Far end asked us to transfer (REFER) — follow it by default, like a desk phone.
    session.on('refer', (e) =>
      e.accept((newSession: RTCSession) => this.adoptReferred(newSession), {
        mediaConstraints: { audio: AUDIO_CONSTRAINTS, video: false },
        pcConfig: PC_CONFIG,
        sessionTimersExpires: SESSION_EXPIRES,
      }),
    );

    this.emit();
    this.syncTones();
    return c;
  }

  private adoptReferred(session: RTCSession): void {
    const uri = session.remote_identity?.uri;
    this.track(session, {
      id: session.id,
      direction: 'outgoing',
      number: uri?.user ?? '',
      name: session.remote_identity?.display_name ?? '',
      state: 'dialing',
      muted: false,
      held: false,
      remoteHeld: false,
      earlyMedia: false,
      startedAt: Date.now(),
    });
  }

  async call(target: string): Promise<string> {
    const ua = this.ua;
    if (!ua || !this.cfg) throw new Error('Not connected');
    const uri = toSipTarget(target, this.cfg.domain);
    if (!uri) throw new Error(`Can't dial "${target}"`);
    tones.unlock();

    // Hold whatever is currently active — one talking call at a time.
    for (const c of this.calls.values()) if (c.info.state === 'active' && !c.info.held) this.hold(c.info.id);

    const stream = await this.getMic();
    const session = ua.call(uri, {
      mediaStream: stream,
      pcConfig: PC_CONFIG,
      sessionTimersExpires: SESSION_EXPIRES,
      rtcOfferConstraints: { offerToReceiveAudio: true, offerToReceiveVideo: false },
    });
    const userPart = /^sips?:([^@;]+)/i.exec(uri)?.[1] ?? target;
    this.track(
      session,
      {
        id: session.id,
        direction: 'outgoing',
        number: decodeURIComponent(userPart),
        name: '',
        state: 'dialing',
        muted: false,
        held: false,
        remoteHeld: false,
        earlyMedia: false,
        startedAt: Date.now(),
      },
      stream,
    );
    return session.id;
  }

  private handleIncoming(session: RTCSession): void {
    const id = session.remote_identity;
    this.track(session, {
      id: session.id,
      direction: 'incoming',
      number: id?.uri?.user ?? 'Unknown',
      name: id?.display_name ?? '',
      state: 'incoming',
      muted: false,
      held: false,
      remoteHeld: false,
      earlyMedia: false,
      startedAt: Date.now(),
    });
  }

  async answer(id: string): Promise<void> {
    const c = this.calls.get(id);
    if (!c || c.info.state !== 'incoming') return;
    tones.unlock();
    this.update(id, { state: 'answering' });
    for (const other of this.calls.values()) {
      if (other.info.id !== id && other.info.state === 'active' && !other.info.held) this.hold(other.info.id);
    }
    try {
      const stream = await this.getMic();
      if (c.session.isEnded()) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      c.localStream = stream;
      c.session.answer({
        mediaStream: stream,
        pcConfig: PC_CONFIG,
        sessionTimersExpires: SESSION_EXPIRES,
      });
    } catch (err) {
      c.session.terminate({ status_code: 480, reason_phrase: 'Microphone unavailable' });
      throw err;
    }
  }

  hangup(id: string): void {
    const c = this.calls.get(id);
    if (!c) return;
    try {
      if (c.info.state === 'incoming') c.session.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
      else c.session.terminate();
    } catch {
      this.cleanup(id, 'Terminated', 'local');
    }
  }

  hangupAll(): void {
    [...this.calls.keys()].forEach((id) => this.hangup(id));
  }

  hold(id: string): void {
    const c = this.calls.get(id);
    if (c?.session.isEstablished() && !c.info.held) c.session.hold();
  }

  unhold(id: string): void {
    const c = this.calls.get(id);
    if (!c?.session.isEstablished() || !c.info.held) return;
    for (const other of this.calls.values()) {
      if (other.info.id !== id && other.info.state === 'active' && !other.info.held) this.hold(other.info.id);
    }
    c.session.unhold();
  }

  toggleHold(id: string): void {
    const c = this.calls.get(id);
    if (c) c.info.held ? this.unhold(id) : this.hold(id);
  }

  toggleMute(id: string): void {
    const c = this.calls.get(id);
    if (!c) return;
    if (c.session.isMuted().audio) c.session.unmute({ audio: true });
    else c.session.mute({ audio: true });
  }

  /**
   * Send DTMF (RFC 2833). Digits pressed before the media path is up (the first ~1s after answer,
   * while DTLS completes) can't be sent yet — the browser throws — so they're queued and flushed in
   * order as soon as it can. Without this, "press 1" pressed right after answer was silently lost.
   */
  sendDtmf(id: string, digit: string): void {
    const c = this.calls.get(id);
    if (!c || c.info.state === 'ended') return;
    c.dtmfQueue = (c.dtmfQueue ?? '') + digit;
    this.flushDtmf(c);
  }

  private flushDtmf(c: Internal): void {
    window.clearTimeout(c.dtmfTimer);
    if (!c.dtmfQueue || c.info.state === 'ended') return;
    const sender = c.session.connection?.getSenders().find((s) => s.track?.kind === 'audio');
    if (c.session.isEstablished() && sender?.dtmf?.canInsertDTMF) {
      const tones = c.dtmfQueue;
      c.dtmfQueue = '';
      try {
        c.session.sendDTMF(tones, { transportType: JsSIP.C.DTMF_TRANSPORT.RFC2833, duration: 120, interToneGap: 70 });
      } catch {
        c.dtmfQueue = tones + c.dtmfQueue; // e.g. previous tones still playing — retry shortly
      }
    }
    if (c.dtmfQueue) c.dtmfTimer = window.setTimeout(() => this.flushDtmf(c), 150);
  }

  /** Blind transfer (REFER). The PBX takes the call over; our leg is released once it accepts. */
  blindTransfer(id: string, target: string): void {
    const c = this.calls.get(id);
    if (!c?.session.isEstablished() || !this.cfg) return;
    const uri = toSipTarget(target, this.cfg.domain);
    if (!uri) throw new Error(`Can't transfer to "${target}"`);
    this.update(id, { transfer: { target, status: 'pending' } });
    c.session.refer(uri, {
      eventHandlers: {
        requestFailed: () => this.update(id, { transfer: { target, status: 'failed' } }),
        accepted: () => {
          this.update(id, { transfer: { target, status: 'accepted' } });
          window.setTimeout(() => this.hangup(id), 300);
        },
        failed: () => this.update(id, { transfer: { target, status: 'failed' } }),
      },
    });
  }

  /**
   * Attended transfer: `id` (usually held) is connected to the party on `consultId`
   * via REFER with Replaces. Both of our legs are released on success.
   */
  attendedTransfer(id: string, consultId: string): void {
    const c = this.calls.get(id);
    const consult = this.calls.get(consultId);
    if (!c?.session.isEstablished() || !consult?.session.isEstablished()) return;
    const label = consult.info.number;
    const target = replacesTarget(consult.session);
    this.update(id, { transfer: { target: label, status: 'pending' } });
    c.session.refer(target, {
      eventHandlers: {
        requestFailed: () => this.update(id, { transfer: { target: label, status: 'failed' } }),
        accepted: () => {
          this.update(id, { transfer: { target: label, status: 'accepted' } });
          window.setTimeout(() => {
            this.hangup(id);
            this.hangup(consultId);
          }, 300);
        },
        failed: () => this.update(id, { transfer: { target: label, status: 'failed' } }),
      },
    });
  }

  /** Switch output device for all current and future calls. */
  setOutputDevice(deviceId: string): void {
    if (this.cfg) this.cfg.outputDeviceId = deviceId;
    for (const c of this.calls.values()) {
      const a = c.audio as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
      a.setSinkId?.(deviceId).catch(() => undefined);
    }
  }

  /** Route live calls to `deviceId` for the rest of this call (null = back to the preferred output). */
  setCallOutput(deviceId: string | null): void {
    this.callOutput = deviceId;
    const target = deviceId ?? this.cfg?.outputDeviceId ?? '';
    for (const c of this.calls.values()) {
      const a = c.audio as HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> };
      a.setSinkId?.(target).catch(() => undefined);
    }
    this.emit();
  }

  get currentCallOutput(): string | null {
    return this.callOutput;
  }

  /** Switch microphone mid-call without renegotiation (replaceTrack). */
  async setInputDevice(deviceId: string): Promise<void> {
    if (this.cfg) this.cfg.inputDeviceId = deviceId;
    for (const c of this.calls.values()) {
      const pc = c.session.connection;
      if (!pc || c.info.state === 'ended') continue;
      const stream = await this.getMic();
      const track = stream.getAudioTracks()[0];
      if (c.info.muted) track.enabled = false;
      const sender = pc.getSenders().find((s) => s.track?.kind === 'audio' || s.track === null);
      await sender?.replaceTrack(track);
      c.localStream?.getTracks().forEach((t) => t.stop());
      c.localStream = stream;
    }
  }

  async getStats(id: string): Promise<CallStats | null> {
    const pc = this.calls.get(id)?.session.connection;
    if (!pc) return null;
    const report = await pc.getStats();
    const out: CallStats = { packetsReceived: 0, packetsLost: 0, packetsSent: 0, bytesReceived: 0 };
    let codecId: string | undefined;
    let outCodecId: string | undefined;
    let pairId: string | undefined;
    const all = new Map<string, Record<string, unknown>>();
    report.forEach((s: Record<string, unknown>) => {
      all.set(s.id as string, s);
      if (s.type === 'inbound-rtp' && s.kind === 'audio') {
        out.packetsReceived = (s.packetsReceived as number) ?? 0;
        out.packetsLost = (s.packetsLost as number) ?? 0;
        out.bytesReceived = (s.bytesReceived as number) ?? 0;
        out.jitterMs = typeof s.jitter === 'number' ? s.jitter * 1000 : undefined;
        out.audioLevel = s.audioLevel as number | undefined;
        out.totalAudioEnergy = s.totalAudioEnergy as number | undefined;
        codecId = s.codecId as string | undefined;
      }
      if (s.type === 'outbound-rtp' && s.kind === 'audio') {
        out.packetsSent = (s.packetsSent as number) ?? 0;
        outCodecId = s.codecId as string | undefined;
      }
      if (s.type === 'transport') {
        pairId = s.selectedCandidatePairId as string | undefined;
        out.dtlsState = s.dtlsState as string | undefined;
        out.srtpCipher = s.srtpCipher as string | undefined;
      }
      if (s.type === 'candidate-pair' && s.nominated && s.state === 'succeeded' && !pairId) pairId = s.id as string;
    });
    const codec = all.get(codecId ?? outCodecId ?? '');
    if (codec) {
      out.codec = String(codec.mimeType ?? '').replace(/^audio\//i, '');
      out.clockRate = codec.clockRate as number | undefined;
    }
    const pair = pairId ? all.get(pairId) : undefined;
    if (pair) {
      if (typeof pair.currentRoundTripTime === 'number') out.rttMs = pair.currentRoundTripTime * 1000;
      const local = all.get(pair.localCandidateId as string);
      out.localCandidateType = local?.candidateType as string | undefined;
    }
    return out;
  }

  // ---------- teardown / tones ----------

  private cleanup(id: string, cause?: string, originator?: string): void {
    const c = this.calls.get(id);
    if (!c || c.info.state === 'ended') return;
    window.clearTimeout(c.iceTimer);
    window.clearTimeout(c.dtmfTimer);
    c.localStream?.getTracks().forEach((t) => t.stop());
    c.audio.pause();
    c.audio.srcObject = null;
    const wasTalking = c.info.state === 'active';
    c.info = {
      ...c.info,
      state: 'ended',
      endedAt: Date.now(),
      endCause: cause,
      endedBy: (originator as CallInfo['endedBy']) ?? 'system',
    };
    const ended = { ...c.info };
    this.endListeners.forEach((l) => l({ call: ended }));
    if (originator !== 'local' && (wasTalking || c.info.direction === 'outgoing')) tones.endBeep();
    this.emit();
    this.syncTones();
    // Keep the "Call ended" card visible briefly, then drop it.
    window.setTimeout(() => {
      this.calls.delete(id);
      if (this.calls.size === 0) this.callOutput = null; // Speaker resets after the call, like a phone
      this.emit();
    }, 1500);
  }

  /** Decide which local tone (if any) should be playing given all call states. */
  private syncTones(): void {
    const live = [...this.calls.values()].map((c) => c.info).filter((i) => i.state !== 'ended');
    const ringingIn = live.some((i) => i.state === 'incoming');
    const talking = live.some((i) => i.state === 'active' || i.state === 'answering');
    const needRingback = live.some((i) => i.state === 'ringing' && !i.earlyMedia);

    if (ringingIn) tones.play(talking ? 'waiting' : 'ringtone');
    else if (needRingback) tones.play('ringback');
    else tones.stop();
  }
}

/**
 * Offer `a=setup:active` instead of `actpass`, so FreeSWITCH answers `passive` and the *browser*
 * starts the DTLS handshake.
 *
 * Otherwise FreeSWITCH takes the client role and fires its ClientHello the moment it sends 183/200 —
 * before the browser's ICE checks have opened the NAT pinhole. That first flight is dropped and
 * FreeSWITCH's DTLS retransmit backoff (1s, 2s, 4s, 8s…) delayed audio by 10–15 s on real networks.
 * Chrome only starts the handshake once ICE is connected, so it goes through first time.
 * (Only the SDP sent to the PBX changes; the local description stays JSEP-valid `actpass`.)
 */
export function preferDtlsClient(sdp: string): string {
  return sdp.replace(/^a=setup:actpass\r?$/gm, (line) => line.replace('actpass', 'active'));
}

/**
 * Refer-To target carrying a Replaces header for `session`'s dialog.
 *
 * JsSIP's own `refer(…, { replaces })` copies the tags from the original INVITE's From/To, which is
 * only right when *we* placed that call. RFC 3891 §3 says the recipient compares to-tag with its
 * local tag and from-tag with its remote tag — i.e. from our side to-tag = our remote tag and
 * from-tag = our local tag, whichever direction the call went. With JsSIP's version FreeSWITCH
 * can't find a dialog we *received*, and the attended transfer drops the caller.
 */
function replacesTarget(session: RTCSession): JsSIP.URI {
  const dialog = (session as unknown as { _dialog?: { id: { call_id: string; local_tag: string; remote_tag: string } } })._dialog;
  const uri = session.remote_identity.uri.clone();
  if (!dialog) throw new Error('Consult call has no confirmed dialog');
  const { call_id, local_tag, remote_tag } = dialog.id;
  uri.setHeader('Replaces', encodeURIComponent(`${call_id};to-tag=${remote_tag};from-tag=${local_tag}`));
  return uri;
}

export const sipClient = new SipClient();

// Handy for debugging from DevTools and for the end-to-end tests.
(window as unknown as { sipClient: SipClient }).sipClient = sipClient;
